/**
 * KLIPY API Hook
 * Provides methods to fetch trending and search GIFs
 */

import { useState, useCallback, useRef } from 'react'
import type { KlipyGif, KlipyApiResponse } from './types'

const KLIPY_APP_KEY = '90363MXbvbQNcgPtFGhdukgjJurpfTwinM4yHVDZBf8E3Fm9WIWvbbqFU6Mq7NdO'
const KLIPY_BASE_URL = `https://api.klipy.com/api/v1/${KLIPY_APP_KEY}/gifs`

interface UseKlipyApiOptions {
  customerId: string
}

export function useKlipyApi({ customerId }: UseKlipyApiOptions) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cancel any pending requests
  const cancelPending = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }, [])

  // Fetch trending GIFs
  const fetchTrending = useCallback(async (page = 1, perPage = 24): Promise<KlipyGif[]> => {
    cancelPending()
    setIsLoading(true)
    setError(null)

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        customer_id: customerId || 'anonymous',
        format_filter: 'webp'
      })

      const response = await fetch(`${KLIPY_BASE_URL}/trending?${params}`, {
        signal: controller.signal
      })

      if (!response.ok) {
        throw new Error('Failed to load trending GIFs')
      }

      const data: KlipyApiResponse = await response.json()

      if (!data.result || !data.data?.data) {
        throw new Error('Invalid response from GIF service')
      }

      return data.data.data
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return []
      }
      const message = err instanceof Error ? err.message : 'Failed to load GIFs'
      setError(message)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [customerId, cancelPending])

  // Search GIFs
  const searchGifs = useCallback(async (query: string, page = 1, perPage = 24): Promise<KlipyGif[]> => {
    if (!query || query.length < 2) {
      return []
    }

    cancelPending()
    setIsLoading(true)
    setError(null)

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const params = new URLSearchParams({
        q: query,
        page: String(page),
        per_page: String(perPage),
        customer_id: customerId || 'anonymous',
        format_filter: 'webp'
      })

      const response = await fetch(`${KLIPY_BASE_URL}/search?${params}`, {
        signal: controller.signal
      })

      if (!response.ok) {
        throw new Error('Failed to search GIFs')
      }

      const data: KlipyApiResponse = await response.json()

      if (!data.result || !data.data?.data) {
        throw new Error('Invalid response from GIF service')
      }

      return data.data.data
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return []
      }
      const message = err instanceof Error ? err.message : 'Failed to search GIFs'
      setError(message)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [customerId, cancelPending])

  return {
    fetchTrending,
    searchGifs,
    isLoading,
    error,
    cancelPending
  }
}
